import React, {Fragment, useContext, useMemo, useState} from 'react';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import Popover from 'patient-ping-remedy/packages/popover';
import {FeatureToggle, Roster, User} from '@/api/dto/dto';
import Avatar from '@common/Avatar';
import {Wrapper} from '@clients/AssignedToClient.styles';
import AvatarDetails from "@common/AvatarDetails";
import {
  CareJourneyResponses,
  CareJourneyResponse,
  CareJourneyDetailsResponse,
  CareJourneyUserAssignment,
} from "@/api/dto/care-journey";
import {StandardTypography} from "patient-ping-remedy/packages/typography";
import {useAuthStore} from "@/store/auth_store";
import {useActiveRoster} from "@/store/roster_store";
import AssignedToList, {AssignUserParams} from "@common/AssignedToList";
import {InfiniteData, useMutation, useQueryClient} from "@tanstack/react-query";
import {MixpanelEventType, MixpanelStepEvent, MixpanelTaskAssignmentEvent} from "@/api/dto/mixpanel";
import {AxiosError} from "axios";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import Helpers from "@/helpers/helpers";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useMixpanel} from "@/hooks/useMixpanel";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import ConfirmAIUnassignModal from "@common/ConfirmAIUnassignModal";
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {useOptimisticUpdate} from "@/hooks/useOptimisticUpdate";

export const aiAgentUser: Partial<User> = {
  first_name: 'Automated',
  last_name: 'Agent',
  image: '/favicon.png',
};

type Props = {
  careJourney: CareJourneyDetailsResponse | CareJourneyResponse;
  showAiAssignment?: boolean;
  roster?: Roster;
}

function mutateUserAssignment(
  careJourneyInstanceId: string,
  careJourneys: InfiniteData<{ data: CareJourneyResponses }> | undefined,
  mutate: (careJourney: CareJourneyResponse) => Partial<CareJourneyUserAssignment>,
) {
  const pageIndex = careJourneys?.pages.findIndex((page) =>
    page.data.careJourneys.some((careJourney) =>
      careJourney.careJourneyInstanceId === careJourneyInstanceId)) ?? 0;
  const index = careJourneys?.pages[pageIndex]?.data.careJourneys.findIndex((careJourney) =>
    careJourney.careJourneyInstanceId === careJourneyInstanceId) ?? -1;

  if (index === -1 || !careJourneys) {
    return careJourneys;
  }

  return {
    ...careJourneys,
    pages: [
      ...careJourneys.pages.slice(0, pageIndex),
      {
        ...careJourneys.pages[pageIndex],
        data: {
          ...careJourneys.pages[pageIndex].data,
          careJourneys: [
            ...careJourneys.pages[pageIndex].data.careJourneys.slice(0, index),
            {
              ...careJourneys.pages[pageIndex].data.careJourneys[index],
              userAssignment: {
                ...careJourneys.pages[pageIndex].data.careJourneys[index].userAssignment,
                ...mutate(careJourneys.pages[pageIndex].data.careJourneys[index]),
              },
            },
            ...careJourneys.pages[pageIndex].data.careJourneys.slice(index + 1),
          ],
        },
      },
      ...careJourneys.pages.slice(pageIndex + 1),
    ],
  };
}

export default function AssignedToTask(props: Props) {
  const  maxCount = 2;
  const maxCountWithoutSelf = maxCount - 1;
  const {currentUser} = useAuthStore();
  const currentRoster = useActiveRoster();
  const {carecoApi} = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const [assignedListOpen, setAssignedListOpen] = useState<boolean>(false);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();
  const [AiUnassignModalOpen, setConfirmAIUnassignModalOpen] = useState<boolean>(false);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const effectiveRoster = props.roster ?? currentRoster;

  const { mutateAsync: assignUser } = useMutation(useOptimisticUpdate<
    InfiniteData<{ data: CareJourneyResponses }>,
    AssignUserParams
  >({
    mutationFn: async (params: AssignUserParams) => {
      return params.delete
        ? carecoApi?.deleteTaskUserAssignment(props.careJourney.careJourneyInstanceId, params.user.id, effectiveRoster!.id)
        : carecoApi?.postTaskUserAssignment(props.careJourney.careJourneyInstanceId, params.user.id, effectiveRoster!.id);
    },
    onSuccess: (_, params) => {
      void queryClient.invalidateQueries({ queryKey: ['careJourneyDetails', props.careJourney?.careJourneyInstanceId] });
      carecoApi?.getCareJourney(props.careJourney!.careJourneyInstanceId, effectiveRoster!.id).then(careJourneyDetails => {
        if (currentUser && !params.delete && mixpanelEventProperties && careJourneyDetails) {
          const eventName =
             params.user.id === currentUser.id
               ? MixpanelEventType.BIH_ASSIGNED_TASK_TO_SELF
               : MixpanelEventType.BIH_ASSIGN_TASK_TO_HUMAN;

          const event: MixpanelTaskAssignmentEvent = {
            eventName,
            eventType: careJourneyDetails.eventType,
            careJourneyName: careJourneyDetails.journeyType,
            assignedTo: params.user.email,
            ...mixpanelEventProperties,
            ...getCurrentGroupDetails(effectiveRoster!)
          };
          carecoApi?.postMixpanelEvent(event);
        }
      });
    },
    onError: (error: AxiosError, params) => {
      Helpers.alertOnError( params.delete ? `Failed to unassign user from task. ${GENERICERROR} ${Helpers.traceId(error)}` :
        `Failed to assign user to task. ${GENERICERROR} ${Helpers.traceId(error)}`,
      error
      );
    },
    optimisticUpdateQueryKey: [['careJourneysData'], ['careJourneysPreReviewData']],
    onOptimisticUpdate(careJourneys, params) {
      return mutateUserAssignment(props.careJourney.careJourneyInstanceId, careJourneys, (careJourney) => ({
        users: params.delete
          ? careJourney.userAssignment.users?.filter((user) => user.id !== params.user.id)
          : [...(careJourney.userAssignment.users || []), params.user],
      }));
    },
  }));

  const assignOrUnassignToAi = useMutation(useOptimisticUpdate<
    InfiniteData<{ data: CareJourneyResponses }>,
    boolean
  >({
    mutationFn: async (reAssignToAi: boolean) => {
      return reAssignToAi
        ? carecoApi?.assignToAi(props.careJourney.careJourneyInstanceId, effectiveRoster!.id)
        : carecoApi?.unassignAI(props.careJourney.careJourneyInstanceId, effectiveRoster!.id);
    },
    onSuccess: (_, reAssignToAi) => {
      void queryClient.invalidateQueries({ queryKey: ['careJourneyDetails', props.careJourney?.careJourneyInstanceId] });
      if (mixpanelEventProperties) {
        carecoApi?.getCareJourney(props.careJourney.careJourneyInstanceId, effectiveRoster!.id).then(careJourneyDetails => {
          if (careJourneyDetails) {
            const event: MixpanelStepEvent = {
              eventName: reAssignToAi
                ? MixpanelEventType.BIH_ASSIGN_TASK_TO_AI
                : MixpanelEventType.BIH_UNASSIGN_AI_FROM_TASK,
              eventType: careJourneyDetails.eventType,
              careJourneyName: careJourneyDetails.journeyType,
              stepName: careJourneyDetails.currentStep,
              ...mixpanelEventProperties,
              ...getCurrentGroupDetails(effectiveRoster!)
            };
            carecoApi?.postMixpanelEvent(event);
          }
        });
      }
    },
    onError: (error: AxiosError, reAssignToAi) => {
      if(reAssignToAi){
        Helpers.alertOnError(`Failed to assign AI ${GENERICERROR} ${Helpers.traceId(error)}`, error);
      }else{
        Helpers.alertOnError(`Failed to unassign AI ${GENERICERROR} ${Helpers.traceId(error)}`, error);
      }
    },
    optimisticUpdateQueryKey: [['careJourneysData'], ['careJourneysPreReviewData']],
    onOptimisticUpdate(careJourneys, reAssignToAi) {
      return mutateUserAssignment(props.careJourney.careJourneyInstanceId, careJourneys, () => ({
        assignedToAi: reAssignToAi,
      }));
    },
  }));

  const userAssignmentsWithoutSelf = useMemo(() => {
    return props.careJourney.userAssignment.users?.filter(userAssignment =>
      userAssignment.id !== currentUser?.id
    );
  }, [props.careJourney.userAssignment]);

  const showAi = props.showAiAssignment ?? true;
  const assignedToAi = showAi && props.careJourney.userAssignment.assignedToAi;
  const assignedToSelf = props.careJourney.userAssignment.users?.length !== userAssignmentsWithoutSelf?.length;

  const content = (
    <Wrapper>
      {assignedToAi &&
        <AvatarDetails user={aiAgentUser} />}
      {assignedToSelf
        ? <AvatarDetails user={currentUser} />
        : <Avatar iconClass={ICONS['user-plus']} />}
      {userAssignmentsWithoutSelf?.
        sort((userAssignment) => userAssignment.id === currentUser?.id ? -1 : 0)
        .map((userAssignment, index) => (
          <Fragment key={index}>
            {index < maxCountWithoutSelf &&
              <AvatarDetails user={userAssignment} />
            }
            {index === maxCountWithoutSelf && (
              <StandardTypography>+{userAssignmentsWithoutSelf.length - maxCountWithoutSelf}</StandardTypography>
            )}
          </Fragment>
        ))
      }
    </Wrapper>
  );

  const canActOnTaskListForRoster = isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      effectiveRoster!,
      FeaturePermissionLevel.ACT,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
      effectiveRoster!,
      FeaturePermissionLevel.ACT,
      Feature.TASKS,
    );

  const canManageOnTaskListForRoster = isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      effectiveRoster!,
      FeaturePermissionLevel.MANAGE,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
      effectiveRoster!,
      FeaturePermissionLevel.MANAGE,
      Feature.TASKS,
    );

  return (
    <>
      {
        showAi && AiUnassignModalOpen &&
        <ConfirmAIUnassignModal
          close={() => setConfirmAIUnassignModalOpen(false)}
          unassignAIAgent={() => {
            assignOrUnassignToAi.mutate(false);
            setConfirmAIUnassignModalOpen(false);
          }}
        />
      }
      <Popover
        isOpen={assignedListOpen}
        toggle={() => setAssignedListOpen(!assignedListOpen)}
        position="bottom-start"
        content={
          <AssignedToList
            assignedUsers={props.careJourney.userAssignment.users || []}
            roster={props.roster}
            assignUser={assignUser}
            assignedToAi={showAi ? assignedToAi : undefined}
            setConfirmAIUnassignModalOpen={showAi ? setConfirmAIUnassignModalOpen : undefined}
            hidePopover={() => setAssignedListOpen(false)}
            canActOnListForRoster={canActOnTaskListForRoster}
            reAssignToAi={showAi ? () => assignOrUnassignToAi.mutate(true) : undefined}
            assigningToAiIsPending={showAi ? assignOrUnassignToAi.isPending : undefined}
            canManageOnListForRoster={canManageOnTaskListForRoster}
            isTask={true}
            status={props.careJourney.status}
          />
        }
      >
        {content}
      </Popover>
    </>
  );
}

